// =============================================================================
// PRODUCTS SECTION COMPONENT (Firestore-based)
// =============================================================================

import React, { useState, useEffect } from 'react';
import { Check, Star, ShoppingCart, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { useShoppingBag } from '@/contexts/ShoppingBagContext';
import { useToast } from '@/contexts/ToastContext';
import { productsService } from '@/services/productsService';
import type { ContentComponent } from '@/types';
import type { Product } from '@/types/products';
import './PricingSection.scss'; // Reuse the same styles

interface ProductsSectionProps {
  component: ContentComponent;
}

/**
 * Products Section Component
 */
export const ProductsSection: React.FC<ProductsSectionProps> = ({ component }) => {
  return <ProductsSectionImpl component={component} />;
};

/**
 * Products Section Implementation
 */
const ProductsSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const columns = metadata?.columns || 2;
  const categoryFilter = metadata?.category; // Optional category filter (string or array of strings)

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let fetchedProducts: Product[];
        if (categoryFilter) {
          // Support both single category string and array of categories
          if (Array.isArray(categoryFilter)) {
            // Fetch products for each category and combine
            const productPromises = categoryFilter.map(cat => 
              productsService.getProductsByCategory(cat)
            );
            const productArrays = await Promise.all(productPromises);
            fetchedProducts = productArrays.flat();
          } else {
            // Single category string
            fetchedProducts = await productsService.getProductsByCategory(categoryFilter);
          }
        } else {
          fetchedProducts = await productsService.getAllProducts();
        }
        
        setProducts(fetchedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryFilter]);

  if (loading) {
    return (
      <section id={id} className="content-section catalog-section">
        <div className="container">
          <div className="section-header">
            <p>Loading products...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id={id} className="content-section catalog-section">
        <div className="container">
          <div className="section-header">
            <p>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  // Convert products to pricing format for compatibility with existing card component
  const pricingFormat = products.map(product => productsService.convertProductToPricingFormat(product));

  return (
    <section id={id} className="content-section catalog-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        {pricingFormat.length === 0 ? (
          <div className="section-header">
            <p>No products available at this time.</p>
          </div>
        ) : (
          <div className={`catalog-grid grid-cols-${columns}`}>
            {pricingFormat.map((category: any, index: number) => (
              <CatalogProductCard key={products[index]?.id || index} category={category} product={products[index]} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Individual Product Card Component (Category with multiple products)
 */
const CatalogProductCard: React.FC<{ category: any; product?: Product }> = ({ category, product }) => {
  // Default to montage image, no product selected
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedSize = category.sizes?.[0] || '';
  const { addItem, openBag } = useShoppingBag();
  const toast = useToast();
  
  const products = category.products || [];
  const currentImage = selectedProduct !== null 
    ? products[selectedProduct]?.image 
    : category.montageImage;
  const currentPrice = selectedProduct !== null 
    ? products[selectedProduct]?.price 
    : null;
  
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  

  // Parse price string to cents
  const parsePrice = (priceStr: string): number => {
    const cleaned = priceStr.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned);
    return Math.round(dollars * 100);
  };

  // Handle adding item to shopping bag
  const handleAddToBag = () => {
    // If no product selected, select first one or show message
    if (selectedProduct === null) {
      if (products.length > 0) {
        // Auto-select first product
        const productToAdd = products[0];
        const price = parsePrice(productToAdd.price);
        const variant = product?.variants?.[0];
        
        addItem({
          categoryName: category.name,
          productName: variant?.variantName || `${category.name} - Option 1`,
          image: productToAdd.image,
          price: price,
          priceDisplay: productToAdd.price,
          size: selectedSize,
          description: category.description,
          categoryDescription: category.description,
          // Include SKU if available from product data
          ...(product && variant?.sku ? { sku: variant.sku } : {}),
        });
        
        toast.success('Added to shopping bag');
        openBag();
      } else {
        toast.error('Please select a product');
      }
      return;
    }

    // Add selected product to bag
    const productToAdd = products[selectedProduct];
    const price = parsePrice(productToAdd.price);
    const variant = product?.variants?.[selectedProduct];
    
    addItem({
      categoryName: category.name,
      productName: variant?.variantName || `${category.name} - Option ${selectedProduct + 1}`,
      image: productToAdd.image,
      price: price,
      priceDisplay: productToAdd.price,
      size: selectedSize,
      description: category.description,
      categoryDescription: category.description,
      // Include SKU if available from product data
      ...(product && variant?.sku ? { sku: variant.sku } : {}),
    });
    
    toast.success('Added to shopping bag');
    openBag();
  };
  
  return (
    <article className={`catalog-product-card ${category.popular ? 'popular' : ''} ${isExpanded ? 'expanded' : ''}`}>
      {category.popular && (
        <div className="product-badge">
          <Star size={16} />
          <span>Most Popular</span>
        </div>
      )}
      
      {/* Product Image Gallery */}
      <div className="product-image-container">
        <div className="product-main-image">
          <img 
            src={currentImage} 
            alt={selectedProduct !== null ? `${category.name} ${selectedProduct + 1}` : `${category.name} Collection`}
            loading="lazy"
          />
          {/* Expand/collapse toggle button */}
          <button
            className="product-image-fullscreen-toggle"
            onClick={handleToggleExpand}
            aria-label={isExpanded ? 'Collapse image' : 'Expand image'}
            type="button"
            title={isExpanded ? 'Collapse image' : 'Expand image'}
          >
            {isExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
        </div>
        {products.length > 0 && (
          <div className="product-image-thumbnails">
            {products.map((product: any, idx: number) => (
              <button
                key={idx}
                type="button"
                className={`thumbnail ${selectedProduct === idx ? 'active' : ''}`}
                onClick={() => setSelectedProduct(idx)}
                aria-label={`View ${category.name} ${idx + 1}`}
              >
                <img src={product.image} alt={`${category.name} ${idx + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Product Info - Hidden when expanded */}
      {!isExpanded && (
        <div className="product-info">
        <div className="product-header">
          <h3 className="product-name">{category.name}</h3>
        </div>
        
        {category.description && (
          <p className="product-description">{category.description}</p>
        )}
        
        {/* Size Display */}
        {category.sizes && category.sizes.length > 0 && (
          <div className="product-size-selector">
            <span className="size-label">Size</span>
            <span className="size-display">{category.sizes[0]}</span>
          </div>
        )}
        
        {/* Product Features */}
        {category.features && category.features.length > 0 && (
          <div className="product-features">
            <ul className="features-list">
              {category.features.slice(0, 4).map((feature: string, featureIndex: number) => (
                <li key={featureIndex} className="feature-item">
                  <Check size={14} className="feature-check" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Add to Bag Button */}
        <div className="product-actions">
          <Button
            variant="primary"
            size="lg"
            onClick={handleAddToBag}
            className="buy-now-button"
            disabled={!currentPrice && products.length === 0}
          >
            <ShoppingCart size={18} />
            Add to Bag
          </Button>
        </div>
      </div>
      )}
    </article>
  );
};

export { ProductsSectionImpl };

